import Url from 'url-parse';
import { combineLatest, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MmAlertService, MmDialogService } from '@metromarkets/components';
import { HttpClient } from '@angular/common/http';

import { ApiConfig } from '~shared/constants/api.config';
import { BuyerAccountService } from '~core/modules/buyer-account';
import {
  ACCOUNT_ADD_BUSINESS_INFORMATION_LINK,
  AUTH_AFTER_EMAIL_VALIDATION_LINK,
  AUTH_AFTER_PASSWORD_RESTORATION_LINK,
} from '~shared/constants/links.constants';

import { AUTHORIZATION_NOTIFICATIONS, AuthorizationTypeEnum } from '../constants/authorization.constant';
import { AuthorizationHttpService } from './authorization-http.service';

@Injectable()
export class AuthorizationService {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private httpClient: HttpClient,
    private alertService: MmAlertService,
    private translateService: TranslateService,
    private authorizationHttpService: AuthorizationHttpService
  ) {}

  public register(registrationType: AuthorizationTypeEnum, registrationData: any): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let registration$: Observable<any>;

    switch (registrationType) {
      case AuthorizationTypeEnum.registerWithMetroCard:
        registration$ = this.authorizationHttpService.registerMetroCardholder(registrationData);
        break;
      case AuthorizationTypeEnum.registerWithoutMetroCard:
        registration$ = this.authorizationHttpService.registerNewEntrepreneur(registrationData);
        break;
      default:
        registration$ = this.authorizationHttpService.registerConsumer(registrationData);
    }

    return registration$.pipe(
      tap(() => {
        this.showSuccessRegistrationAlert();
        this.handleSuccessAuthorization(registrationType);
      })
    );
  }

  public sendResetPasswordEmail(email: string): Observable<any> {
    const body = { email };
    return this.httpClient
      .post(`${ApiConfig.SVC_IMS_API_URL()}/accounts/send-reset-password-email`, body)
      .pipe(tap(() => this.router.navigateByUrl(AUTH_AFTER_PASSWORD_RESTORATION_LINK)));
  }

  public resendEmailValidation(email: string): Observable<any> {
    const body = { email };
    return this.httpClient
      .post(`${ApiConfig.SVC_IMS_API_URL()}/accounts/resend-activation`, body)
      .pipe(tap(() => this.router.navigateByUrl(AUTH_AFTER_EMAIL_VALIDATION_LINK)));
  }

  private handleSuccessAuthorization(authorizationType: AuthorizationTypeEnum): void {
    const redirectUrlString = this.getRedirectUrl(authorizationType);
    const redirectUrl = new Url(redirectUrlString, true);
    this.router.navigate([redirectUrl.pathname], {
      replaceUrl: true,
      queryParams: redirectUrl.query,
    });
  }

  private showSuccessRegistrationAlert(): void {
    const successRegistrationMessage$ = this.translateService.get(AUTHORIZATION_NOTIFICATIONS.successRegistration);
    const accountValidationMessage$ = this.translateService.get(AUTHORIZATION_NOTIFICATIONS.accountValidation);

    combineLatest([successRegistrationMessage$, accountValidationMessage$]).subscribe(
      ([successRegistrationMessage, accountValidationMessage]) => {
        this.alertService.addAlert({
          title: successRegistrationMessage,
          message: accountValidationMessage,
          type: 'success',
          mode: 'banner',
          size: 'large',
          contentAlign: 'center',
        });
      }
    );
  }

  private getRedirectUrl(registrationType: AuthorizationTypeEnum): string {
    const backUrl = this.activatedRoute.snapshot.queryParams.backUrl || '/';

    const shouldRedirectToAddBusinessInfo = registrationType === AuthorizationTypeEnum.registerWithoutMetroCard;
    if (shouldRedirectToAddBusinessInfo) {
      return `${ACCOUNT_ADD_BUSINESS_INFORMATION_LINK}?backUrl=${backUrl}`;
    }

    return backUrl;
  }
}
